/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

// Titles definition for Bioindex
// Each title has a stable id, display label, and unlock condition

export const TITLES = [
  {
    id: "first_upload",
    label: "First Capture",
    description: "Upload your first photo",
    condition: (userData) => userData.totalPhotos >= 1,
  },

  {
    id: "species_10",
    label: "Budding Naturalist",
    description: "Record 10 species",
    condition: (userData) => userData.totalSpecies >= 10,
  },

  {
    id: "species_25",
    label: "Amateur Photographer",
    description: "Record 25 species",
    condition: (userData) => userData.totalSpecies >= 25,
  },

  {
    id: "photos_50",
    label: "Experienced Photographer",
    description: "Upload 50 photos",
    condition: (userData) => userData.totalPhotos >= 50,
  },

  {
    id: "species_100",
    label: "The Curator",
    description: "Record 100 species",
    condition: (userData) => userData.totalSpecies >= 100,
  },

  {
    id: "genera_100",
    label: "The General",
    description: "Record 100 genera",
    condition: (userData) => userData.totalGenera >= 100,
  },

  {
    id: "photos_250",
    label: "The Roaming Naturalist",
    description: "Upload 250 photos",
    condition: (userData) => userData.totalPhotos >= 250,
  },

  {
    id: "photos_500",
    label: "The Snap Trekker",
    description: "Upload 500 photos",
    condition: (userData) => userData.totalPhotos >= 500,
  },

  {
    id: "photos_750",
    label: "The Frame Rover",
    description: "Upload 750 photos",
    condition: (userData) => userData.totalPhotos >= 750,
  },

  {
    id: "photos_1000",
    label: "Biodiversity Archivist",
    description: "Upload 1,000 photos",
    condition: (userData) => userData.totalPhotos >= 1000,
  },

  {
    id: "photos_1250",
    label: "Seeker of Light",
    description: "Upload 1,250 photos",
    condition: (userData) => userData.totalPhotos >= 1250,
  },

  {
    id: "photos_1500",
    label: "Keeper of Light",
    description: "Upload 1,500 photos",
    condition: (userData) => userData.totalPhotos >= 1500,
  },

  {
    id: "photos_2000",
    label: "Master of Light",
    description: "Upload 2,000 photos",
    condition: (userData) => userData.totalPhotos >= 2000,
  },

  {
    id: "photos_3000",
    label: "Biosteward",
    description: "Upload 3,000 photos",
    condition: (userData) => userData.totalPhotos >= 3000,
  },

  // 🌍 KINGDOM-LEVEL (broad mastery)

  {
    id: "kingdoms_3",
    label: "The Three Kings",
    description: "Record species across Animalia, Plantae, and Fungi",
    condition: (userData) => Object.keys(userData.byKingdom || {}).length >= 3,
  },

  {
    id: "fungi_5",
    label: "Toad",
    description: "Record 5 fungi species",
    condition: (userData) => (userData.byKingdom?.fungi || 0) >= 5,
  },
  {
    id: "fungi_25",
    label: "Toadette",
    description: "Record 25 fungi species",
    condition: (userData) => (userData.byKingdom?.fungi || 0) >= 25,
  },
  {
    id: "fungi_50",
    label: "Toadsworth",
    description: "Record 50 fungi species",
    condition: (userData) => (userData.byKingdom?.fungi || 0) >= 50,
  },
  {
    id: "fungi_100",
    label: "Princess Toadstool",
    description: "Record 100 fungi species",
    condition: (userData) => (userData.byKingdom?.fungi || 0) >= 100,
  },

  {
    id: "fungi_250",
    label: "The Mushroom Kingdom",
    description: "Record 250 fungi species",
    condition: (userData) => (userData.byKingdom?.fungi || 0) >= 250,
  },
  {
    id: "animalia_25",
    label: "Bambi",
    description: "Record 25 animal species",
    condition: (userData) => (userData.byKingdom?.animalia || 0) >= 25,
  },

  {
    id: "animalia_100",
    label: "Dr. Dolittle",
    description: "Record 100 animal species",
    condition: (userData) => (userData.byKingdom?.animalia || 0) >= 100,
  },

  {
    id: "animalia_350",
    label: "Noah's Archivist",
    description: "Record 350 animal species",
    condition: (userData) => (userData.byKingdom?.animalia || 0) >= 350,
  },

  {
    id: "animalia_500",
    label: "The Beastmaster",
    description: "Record 500 animal species",
    condition: (userData) => (userData.byKingdom?.animalia || 0) >= 500,
  },

  {
    id: "animalia_1000",
    label: "Planet Earth",
    description: "Record 1,000 animal species",
    condition: (userData) => (userData.byKingdom?.animalia || 0) >= 1000,
  },

  {
    id: "plantae_25",
    label: "Plant Parent",
    description: "Record 25 plant species",
    condition: (userData) => (userData.byKingdom?.plantae || 0) >= 25,
  },

  {
    id: "plantae_100",
    label: "The Lorax",
    description: "Record 100 plant species",
    condition: (userData) => (userData.byKingdom?.plantae || 0) >= 100,
  },

  {
    id: "plantae_250",
    label: "Audrey II",
    description: "Record 250 plant species",
    condition: (userData) => (userData.byKingdom?.plantae || 0) >= 250,
  },

  {
    id: "plantae_500",
    label: "Green Thumb",
    description: "Record 500 plant species",
    condition: (userData) => (userData.byKingdom?.plantae || 0) >= 500,
  },

  {
    id: "plantae_1000",
    label: "Treebeard",
    description: "Record 1,000 plant species",
    condition: (userData) => (userData.byKingdom?.plantae || 0) >= 1000,
  },

  // PHYLUM-LEVEL
  {
    id: "chordata_100",
    label: "Spine Tingler",
    description: "Record 100 Chordata species",
    condition: (userData) => (userData.byPhylum?.chordata || 0) >= 100,
  },

  {
    id: "chordata_500",
    label: "Backbone Empire",
    description: "Record 500 Chordata species",
    condition: (userData) => (userData.byPhylum?.chordata || 0) >= 500,
  },

  {
    id: "arthropoda_100",
    label: "Bug Life",
    description: "Record 100 Arthropoda species",
    condition: (userData) => (userData.byPhylum?.arthropoda || 0) >= 100,
  },

  {
    id: "arthropoda_500",
    label: "Exoskeleton Crew",
    description: "Record 500 Arthropoda species",
    condition: (userData) => (userData.byPhylum?.arthropoda || 0) >= 500,
  },

  {
    id: "mollusca_1",
    label: "Gary",
    description: "Record 1 Mollusca species",
    condition: (userData) => (userData.byPhylum?.mollusca || 0) >= 1,
  },

  {
    id: "mollusca_25",
    label: "Escargot Starter",
    description: "Record 25 Mollusca species",
    condition: (userData) => (userData.byPhylum?.mollusca || 0) >= 25,
  },

  {
    id: "mollusca_100",
    label: "The Kraken",
    description: "Record 100 Mollusca species",
    condition: (userData) => (userData.byPhylum?.mollusca || 0) >= 100,
  },

  {
    id: "ascomycota_25",
    label: "Yeast Mode",
    description: "Record 25 Ascomycota species",
    condition: (userData) => (userData.byPhylum?.ascomycota || 0) >= 25,
  },

  {
    id: "ascomycota_75",
    label: "Morel Support",
    description: "Record 75 Ascomycota species",
    condition: (userData) => (userData.byPhylum?.ascomycota || 0) >= 75,
  },

  {
    id: "basidiomycota_25",
    label: "Cap Collector",
    description: "Record 25 Basidiomycota species",
    condition: (userData) => (userData.byPhylum?.basidiomycota || 0) >= 25,
  },

  {
    id: "basidiomycota_75",
    label: "Shiitake Happens",
    description: "Record 75 Basidiomycota species",
    condition: (userData) => (userData.byPhylum?.basidiomycota || 0) >= 75,
  },

  {
    id: "glomeromycota_1",
    label: "Root Network",
    description: "Record 1 Glomeromycota species",
    condition: (userData) => (userData.byPhylum?.glomeromycota || 0) >= 1,
  },

  {
    id: "chytridiomycota_1",
    label: "Chytrid Kid",
    description: "Record 1 Chytridiomycota species",
    condition: (userData) => (userData.byPhylum?.chytridiomycota || 0) >= 1,
  },

  {
    id: "zygomycota_1",
    label: "Zygo Stardust",
    description: "Record 1 Zygomycota species",
    condition: (userData) => (userData.byPhylum?.zygomycota || 0) >= 1,
  },

  {
    id: "cnidaria_1",
    label: "Squishy",
    description: "Record 1 Cnidaria species",
    condition: (userData) => (userData.byPhylum?.cnidaria || 0) >= 1,
  },

  {
    id: "cnidaria_10",
    label: "Jellyfish Jam",
    description: "Record 10 Cnidaria species",
    condition: (userData) => (userData.byPhylum?.cnidaria || 0) >= 10,
  },

  {
    id: "cnidaria_50",
    label: "Sting Operation",
    description: "Record 50 Cnidaria species",
    condition: (userData) => (userData.byPhylum?.cnidaria || 0) >= 50,
  },

  {
    id: "magnoliophyta_200",
    label: "Flowering Dominance",
    description: "Record 200 flowering plant species",
    condition: (userData) => (userData.byPhylum?.magnoliophyta || 0) >= 200,
  },

  {
    id: "pteridophyta_50",
    label: "Fern Familiar",
    description: "Record 50 ferns and allies",
    condition: (userData) => (userData.byPhylum?.pteridophyta || 0) >= 50,
  },

  {
    id: "lycophyta_25",
    label: "Spore Specialist",
    description: "Record 25 lycophytes",
    condition: (userData) => (userData.byPhylum?.lycophyta || 0) >= 25,
  },

  {
    id: "pinophyta_25",
    label: "Conifer Curator",
    description: "Record 25 conifers",
    condition: (userData) => (userData.byPhylum?.pinophyta || 0) >= 25,
  },

  {
    id: "marchantiophyta_25",
    label: "Bryophyte Keeper",
    description: "Record 25 liverworts",
    condition: (userData) => (userData.byPhylum?.marchantiophyta || 0) >= 25,
  },

  {
    id: "cycadophyta_5",
    label: "Cycad Custodian",
    description: "Record 5 cycads",
    condition: (userData) => (userData.byPhylum?.cycadophyta || 0) >= 5,
  },
  {
    id: "ginkgophyta_1",
    label: "Ginkgo Unchained",
    description: "Record 1 Ginkgophyta species",
    condition: (userData) => (userData.byPhylum?.ginkgophyta || 0) >= 1,
  },

  {
    id: "echinodermata_1",
    label: "No, This Is Patrick!",
    description: "Record 1 echinoderm species",
    condition: (userData) => (userData.byPhylum?.echinodermata || 0) >= 1,
  },

  {
    id: "echinodermata_10",
    label: "Under the Sea",
    description: "Record 10 echinoderm species",
    condition: (userData) => (userData.byPhylum?.echinodermata || 0) >= 10,
  },

  {
    id: "echinodermata_50",
    label: "Five-Armed Army",
    description: "Record 50 echinoderm species",
    condition: (userData) => (userData.byPhylum?.echinodermata || 0) >= 50,
  },

  // CLASS-LEVEL

  {
    id: "mammalia_25",
    label: "Babe",
    description: "Record 25 Mammalia species",
    condition: (userData) => (userData.byClass?.mammalia || 0) >= 25,
  },

  {
    id: "mammalia_75",
    label: "Simba",
    description: "Record 75 Mammalia species",
    condition: (userData) => (userData.byClass?.mammalia || 0) >= 75,
  },

  {
    id: "mammalia_150",
    label: "Attenborough's Shadow's Shadow",
    description: "Record 150 Mammalia species",
    condition: (userData) => (userData.byClass?.mammalia || 0) >= 150,
  },

  {
    id: "mammalia_300",
    label: "Attenborough's Shadow",
    description: "Record 300 Mammalia species",
    condition: (userData) => (userData.byClass?.mammalia || 0) >= 300,
  },

  {
    id: "aves_25",
    label: "Big Bird",
    description: "Record 25 bird species",
    condition: (userData) => (userData.byClass?.aves || 0) >= 25,
  },

  {
    id: "aves_100",
    label: "The Birdman",
    description: "Record 100 bird species",
    condition: (userData) => (userData.byClass?.aves || 0) >= 100,
  },

  {
    id: "aves_200",
    label: "The Audubon",
    description: "Record 200 bird species",
    condition: (userData) => (userData.byClass?.aves || 0) >= 200,
  },

  {
    id: "aves_500",
    label: "The Ornithologist",
    description: "Record 500 bird species",
    condition: (userData) => (userData.byClass?.aves || 0) >= 500,
  },

  {
    id: "reptilia_10",
    label: "Zilla",
    description: "Record 10 Reptilia species",
    condition: (userData) => (userData.byClass?.reptilia || 0) >= 10,
  },

  {
    id: "reptilia_40",
    label: "Daughter of Phorcys",
    description: "Record 40 Reptilia species",
    condition: (userData) => (userData.byClass?.reptilia || 0) >= 40,
  },

  {
    id: "reptilia_75",
    label: "King Komodo",
    description: "Record 75 Reptilia species",
    condition: (userData) => (userData.byClass?.reptilia || 0) >= 75,
  },

  {
    id: "reptilia_150",
    label: "The Herpetologist",
    description: "Record 150 Reptilia species",
    condition: (userData) => (userData.byClass?.reptilia || 0) >= 150,
  },

  {
    id: "amphibia_5",
    label: "Kermit",
    description: "Record 5 Amphibia species",
    condition: (userData) => (userData.byClass?.amphibia || 0) >= 5,
  },

  {
    id: "amphibia_5",
    label: "René",
    description: "Record 5 Amphibia species",
    condition: (userData) => (userData.byClass?.amphibia || 0) >= 5,
  },

  {
    id: "amphibia_20",
    label: "Frogger",
    description: "Record 20 Amphibia species",
    condition: (userData) => (userData.byClass?.amphibia || 0) >= 20,
  },

  {
    id: "amphibia_50",
    label: "Emphatic Amphibian",
    description: "Record 50 Amphibia species",
    condition: (userData) => (userData.byClass?.amphibia || 0) >= 50,
  },

  {
    id: "actino_25",
    label: "Finding Nemo",
    description: "Record 25 ray-finned fish",
    condition: (userData) => (userData.byClass?.actinopterygii || 0) >= 25,
  },

  {
    id: "actino_150",
    label: "Aquaman",
    description: "Record 150 ray-finned fish",
    condition: (userData) => (userData.byClass?.actinopterygii || 0) >= 150,
  },

  {
    id: "actino_300",
    label: "Master Angler",
    description: "Record 300 ray-finned fish",
    condition: (userData) => (userData.byClass?.actinopterygii || 0) >= 300,
  },

  {
    id: "chondrichthyes_7",
    label: "Shark Week",
    description: "Record 7 cartilaginous fish",
    condition: (userData) => (userData.byClass?.chondrichthyes || 0) >= 7,
  },

  {
    id: "chondrichthyes_100",
    label: "Bruce",
    description: "Record 100 cartilaginous fish",
    condition: (userData) => (userData.byClass?.chondrichthyes || 0) >= 100,
  },

  {
    id: "eudicots_25",
    label: "The Dicot Club",
    legacyLabels: ["Rose Garden"],
    description: "Record 25 eudicot species",
    condition: (userData) => (userData.byClass?.eudicots || 0) >= 25,
  },

  {
    id: "eudicots_100",
    label: "Breaking Bud",
    legacyLabels: ["Dicot Dynasty"],
    description: "Record 100 eudicot species",
    condition: (userData) => (userData.byClass?.eudicots || 0) >= 100,
  },

  {
    id: "eudicots_250",
    label: "Everything Everywhere All in Bloom",
    legacyLabels: ["Flower Power"],
    description: "Record 250 eudicot species",
    condition: (userData) => (userData.byClass?.eudicots || 0) >= 250,
  },
  {
    id: "monocots_25",
    label: "Patient Grasshopper",
    description: "Record 25 monocot species",
    condition: (userData) => (userData.byClass?.monocots || 0) >= 25,
  },

  {
    id: "monocots_100",
    label: "Monocot Mafia",
    description: "Record 100 monocot species",
    condition: (userData) => (userData.byClass?.monocots || 0) >= 100,
  },

  {
    id: "monocots_250",
    label: "Blade Runner",
    description: "Record 250 monocot species",
    condition: (userData) => (userData.byClass?.monocots || 0) >= 250,
  },

  // Order-specific

  {
    id: "fabales_40",
    label: "The Podfather",
    description: "Record 40 Fabales species",
    condition: (userData) => (userData.byOrder?.fabales || 0) >= 40,
  },

  {
    id: "lamiales_40",
    label: "The Lamialean Monster",
    description: "Record 40 Lamiales species",
    condition: (userData) => (userData.byOrder?.lamiales || 0) >= 40,
  },

  {
    id: "malpighiales_50",
    label: "Malpighi’s Apprentice",
    description: "Record 50 Malpighiales species",
    condition: (userData) => (userData.byOrder?.malpighiales || 0) >= 50,
  },

  {
    id: "rosales_40",
    label: "Rose Order Ranger",
    description: "Record 40 Rosales species",
    condition: (userData) => (userData.byOrder?.rosales || 0) >= 40,
  },

  {
    id: "ericales_30",
    label: "Erika",
    description: "Record 30 Ericales species",
    condition: (userData) => (userData.byOrder?.ericales || 0) >= 30,
  },

  {
    id: "gentianales_30",
    label: "Genial",
    description: "Record 30 Gentianales species",
    condition: (userData) => (userData.byOrder?.gentianales || 0) >= 30,
  },

  {
    id: "malvales_30",
    label: "Malevolent",
    description: "Record 30 Malvales species",
    condition: (userData) => (userData.byOrder?.malvales || 0) >= 30,
  },

  {
    id: "ranunculales_20",
    label: "Buttercup",
    description: "Record 20 Ranunculales species",
    condition: (userData) => (userData.byOrder?.ranunculales || 0) >= 20,
  },

  {
    id: "santalales_20",
    label: "Sandalwood Seeker",
    description: "Record 20 Santalales species",
    condition: (userData) => (userData.byOrder?.santalales || 0) >= 20,
  },

  {
    id: "sapindales_20",
    label: "Sappy Papi",
    description: "Record 20 Sapindales species",
    condition: (userData) => (userData.byOrder?.sapindales || 0) >= 20,
  },

  {
    id: "brassicales_20",
    label: "Bruh!",
    description: "Record 20 Brassicales species",
    condition: (userData) => (userData.byOrder?.brassicales || 0) >= 20,
  },

  {
    id: "primates_5",
    label: "Mojo Jojo",
    description: "Record 5 Primates species",
    condition: (userData) => (userData.byOrder?.primates || 0) >= 5,
  },

  {
    id: "carnivora_10",
    label: "Sabor",
    description: "Record 10 Carnivora species",
    condition: (userData) => (userData.byOrder?.carnivora || 0) >= 10,
  },

  {
    id: "rodentia_10",
    label: "Rattata",
    description: "Record 10 Rodentia species",
    condition: (userData) => (userData.byOrder?.rodentia || 0) >= 10,
  },

  {
    id: "chiroptera_10",
    label: "Major League Bats",
    description: "Record 10 Chiroptera species",
    condition: (userData) => (userData.byOrder?.chiroptera || 0) >= 10,
  },
  {
    id: "lagomorpha_10",
    label: "Flopsy's Friend",
    description: "Record 10 Lagomorpha species",
    condition: (userData) => (userData.byOrder?.lagomorpha || 0) >= 10,
  },
  {
    id: "proboscidea_1",
    label: "Dumbo's Feather",
    description: "Record 1 Proboscidea species",
    condition: (userData) => (userData.byOrder?.proboscidea || 0) >= 1,
  },

  {
    id: "passeriformes_50",
    label: "I Love to Singa",
    description: "Record 50 Passeriformes species",
    condition: (userData) => (userData.byOrder?.passeriformes || 0) >= 50,
  },

  {
    id: "passeriformes_150",
    label: "Hector Lavoe",
    description: "Record 150 Passeriformes species",
    condition: (userData) => (userData.byOrder?.passeriformes || 0) >= 150,
  },

  {
    id: "passeriformes_300",
    label: "Master of the Perch",
    description: "Record 500 Passeriformes species",
    condition: (userData) => (userData.byOrder?.passeriformes || 0) >= 500,
  },

  {
    id: "psittaciformes_20",
    label: "Parrot Syndicate",
    description: "Record 20 Psittaciformes species",
    condition: (userData) => (userData.byOrder?.psittaciformes || 0) >= 20,
  },

  {
    id: "strigiformes_20",
    label: "Silent Wing",
    description: "Record 20 Strigiformes species",
    condition: (userData) => (userData.byOrder?.strigiformes || 0) >= 20,
  },

  {
    id: "accipitriformes_20",
    label: "Thermal Rider",
    description: "Record 20 Accipitriformes species",
    condition: (userData) => (userData.byOrder?.accipitriformes || 0) >= 20,
  },

  {
    id: "falconiformes_20",
    label: "Velocity Hunter",
    description: "Record 20 Falconiformes species",
    condition: (userData) => (userData.byOrder?.falconiformes || 0) >= 20,
  },

  {
    id: "piciformes_20",
    label: "Forest Percussionist",
    description: "Record 20 Piciformes species",
    condition: (userData) => (userData.byOrder?.piciformes || 0) >= 20,
  },

  {
    id: "anseriformes_20",
    label: "Wetland Keeper",
    description: "Record 20 Anseriformes species",
    condition: (userData) => (userData.byOrder?.anseriformes || 0) >= 20,
  },

  {
    id: "charadriiformes_20",
    label: "Tide Walker",
    description: "Record 20 Charadriiformes species",
    condition: (userData) => (userData.byOrder?.charadriiformes || 0) >= 20,
  },

  {
    id: "apodiformes_20",
    label: "Airblade",
    description: "Record 20 Apodiformes species",
    condition: (userData) => (userData.byOrder?.apodiformes || 0) >= 20,
  },

  {
    id: "caprimulgiformes_20",
    label: "Dusk Phantom",
    description: "Record 20 Caprimulgiformes species",
    condition: (userData) => (userData.byOrder?.caprimulgiformes || 0) >= 20,
  },

  {
    id: "cuculiformes_20",
    label: "Brood Trickster",
    description: "Record 20 Cuculiformes species",
    condition: (userData) => (userData.byOrder?.cuculiformes || 0) >= 20,
  },

  {
    id: "coraciiformes_20",
    label: "Flash of Color",
    description: "Record 20 Coraciiformes species",
    condition: (userData) => (userData.byOrder?.coraciiformes || 0) >= 20,
  },

  {
    id: "columbiformes_20",
    label: "City Ghost",
    description: "Record 20 Columbiformes species",
    condition: (userData) => (userData.byOrder?.columbiformes || 0) >= 20,
  },

  {
    id: "galliformes_20",
    label: "Ground Sovereign",
    description: "Record 20 Galliformes species",
    condition: (userData) => (userData.byOrder?.galliformes || 0) >= 20,
  },

  {
    id: "gruiformes_20",
    label: "Marsh Strider",
    description: "Record 20 Gruiformes species",
    condition: (userData) => (userData.byOrder?.gruiformes || 0) >= 20,
  },

  {
    id: "pelecaniformes_20",
    label: "Water Sentinel",
    description: "Record 20 Pelecaniformes species",
    condition: (userData) => (userData.byOrder?.pelecaniformes || 0) >= 20,
  },

  {
    id: "suliformes_10",
    label: "Ocean Diver",
    description: "Record 10 Suliformes species",
    condition: (userData) => (userData.byOrder?.suliformes || 0) >= 10,
  },

  {
    id: "procellariiformes_10",
    label: "Storm Navigator",
    description: "Record 10 Procellariiformes species",
    condition: (userData) => (userData.byOrder?.procellariiformes || 0) >= 10,
  },

  {
    id: "sphenisciformes_10",
    label: "Ice Diver",
    description: "Record 10 Sphenisciformes species",
    condition: (userData) => (userData.byOrder?.sphenisciformes || 0) >= 10,
  },

  {
    id: "podicipediformes_20",
    label: "Water Shadow",
    description: "Record 20 Podicipediformes species",
    condition: (userData) => (userData.byOrder?.podicipediformes || 0) >= 20,
  },

  {
    id: "gaviiformes_10",
    label: "Echo Diver",
    description: "Record Gaviiformes",
    condition: (userData) => (userData.byOrder?.gaviiformes || 0) >= 5,
  },

  {
    id: "ciconiiformes_20",
    label: "Sky Carrier",
    description: "Record 20 Ciconiiformes species",
    condition: (userData) => (userData.byOrder?.ciconiiformes || 0) >= 20,
  },

  {
    id: "bucerotiformes_20",
    label: "Beak Herald",
    description: "Record 20 Bucerotiformes species",
    condition: (userData) => (userData.byOrder?.bucerotiformes || 0) >= 20,
  },

  {
    id: "trogoniformes_20",
    label: "Forest Jewel",
    description: "Record 20 Trogoniformes species",
    condition: (userData) => (userData.byOrder?.trogoniformes || 0) >= 20,
  },

  // Family-specific

  {
    id: "bovidae_5",
    label: "Agropecuaria",
    description: "Record 5 Bovidae species",
    condition: (userData) => (userData.byFamily?.bovidae || 0) >= 5,
  },

  {
    id: "asteraceae",
    label: "Sun God",
    description: "Record 10 Asteraceae species",
    condition: (userData) => (userData.byFamily?.asteraceae || 0) >= 10,
  },
  {
    id: "bignoniaceae",
    label: "The Notorious B.I.G.",
    description: "Record 10 Bignoniaceae species",
    condition: (userData) => (userData.byFamily?.bignoniaceae || 0) >= 10,
  },

  {
    id: "moraceae",
    label: "Paramore",
    description: "Record 10 Moraceae species",
    condition: (userData) => (userData.byFamily?.moraceae || 0) >= 10,
  },
  {
    id: "verbenaceae",
    label: "The Verve",
    description: "Record 10 Verbenaceae species",
    condition: (userData) => (userData.byFamily?.verbenaceae || 0) >= 10,
  },
  {
    id: "families_50",
    label: "Family Matters",
    description: "Record species across 50 families",
    condition: (userData) => Object.keys(userData.byFamily || {}).length >= 50,
  },

  {
    id: "fabaceae_30",
    label: "Peas Be With You",
    description: "Record 30 Fabaceae species",
    condition: (userData) => (userData.byFamily?.fabaceae || 0) >= 30,
  },

  {
    id: "psittacidae_1",
    label: "Polly",
    description: "Record 1 Psittacidae species",
    condition: (userData) => (userData.byFamily?.psittacidae || 0) >= 1,
  },

  {
    id: "psittacidae_10",
    label: "Iago",
    description: "Record 10 Psittacidae species",
    condition: (userData) => (userData.byFamily?.psittacidae || 0) >= 10,
  },

  {
    id: "psittacidae_25",
    label: "¿Quiere cacao? Wajajaja. ¡Hola!",
    description: "Record 25 Psittacidae species",
    condition: (userData) => (userData.byFamily?.psittacidae || 0) >= 25,
  },

  {
    id: "thraupidae_10",
    label: "Tanager Trainee",
    description: "Record 10 Thraupidae species",
    condition: (userData) => (userData.byFamily?.["thraupidae"] || 0) >= 10,
  },

  {
    id: "thraupidae_25",
    label: "Tanager Tracker",
    description: "Record 25 Thraupidae species",
    condition: (userData) => (userData.byFamily?.["thraupidae"] || 0) >= 25,
  },

  {
    id: "thraupidae_40",
    label: "The Tanager Manager",
    description: "Record 40 Thraupidae species",
    condition: (userData) => (userData.byFamily?.["thraupidae"] || 0) >= 40,
  },

  {
    id: "Mephitidae_1",
    label: "Funk Collector",
    description: "Add your first Mephitidae species",
    condition: (userData) => (userData.byFamily?.["mephitidae"] || 0) >= 1,
  },
  {
    id: "cardinalidae_1",
    label: "The Cardinal Sin",
    description: "Record 1 Cardinalidae species",
    condition: (userData) => (userData.byFamily?.["cardinalidae"] || 0) >= 1,
  },
  {
    id: "asterales_50",
    label: "Sunny Forelli",
    description: "Record 50 Asterales species",
    condition: (userData) => (userData.byOrder?.asterales || 0) >= 50,
  },

  {
    id: "Steatornithidae_1",
    label: "Oilman",
    description: "Record 1 Steatornithidae species",
    condition: (userData) => (userData.byFamily?.steatornithidae || 0) >= 1,
  },
  {
    id: "Momotidae_1",
    label: "Whoop Whoop!",
    description: "Record 1 Momotidae species",
    condition: (userData) => (userData.byFamily?.momotidae || 0) >= 1,
  },
  {
    id: "Spheniscidae_1",
    label: "Cool. Cool Cool Cool.",
    description: "Record 1 penguin species",
    condition: (userData) => (userData.byFamily?.Spheniscidae || 0) >= 1,
  },
  {
    id: "Sulidae_2",
    label: "The Booby Wrestler",
    description: "Record 2 Sulidae species",
    condition: (userData) => (userData.byFamily?.sulidae || 0) >= 2,
  },
  {
    id: "Tyrannidae_30",
    label: "The Terrible Tyrant",
    description: "Record 30 Tyrannidae species",
    condition: (userData) => (userData.byFamily?.tyrannidae || 0) >= 30,
  },
  {
    id: "Turdidae_10",
    label: "Turd Fan",
    description: "Record 10 Turdidae species",
    condition: (userData) => (userData.byFamily?.turdidae || 0) >= 10,
  },

  {
    id: "Icteridae_10",
    label: "Nest Architect",
    description: "Record 10 Icteridae species",
    condition: (userData) => (userData.byFamily?.icteridae || 0) >= 10,
  },

  {
    id: "Furnariidae_10",
    label: "A Creep, A Weirdo",
    description: "Record 10 Furnariidae species",
    condition: (userData) => (userData.byFamily?.furnariidae || 0) >= 10,
  },

  {
    id: "Hirundinidae_10",
    label: "Tailgater",
    description: "Record 10 Hirundinidae species",
    condition: (userData) => (userData.byFamily?.hirundinidae || 0) >= 10,
  },

  // Genus-specific

  {
    id: "Caecilian_1",
    label: "Sussudio Caecilian",
    description: "Record 1 Caecilian species",
    condition: (userData) => (userData.byGenus?.Gymnopis || 0) >= 1,
  },
  {
    id: "Cannabis_1",
    label: "God’s Lettuce",
    description: "Record 1 Cannabis species",
    condition: (userData) => (userData.byGenus?.Cannabis || 0) >= 1,
  },
  {
    id: "Spizaetus_1",
    label: "Canopy Assassin",
    description: "Record 1 Spizaetus species",
    condition: (userData) => (userData.byGenus?.Spizaetus || 0) >= 1,
  },

  {
    id: "Piper_5",
    label: "Pied Piper of Hamelin",
    description: "Record 5 Piper species",
    condition: (userData) => (userData.byGenus?.Piper || 0) >= 5,
  },
  {
    id: "Ictinia_3",
    label: "Sky Surfer",
    description: "Record 3 Ictinia species",
    condition: (userData) => (userData.byGenus?.Ictinia || 0) >= 3,
  },
  {
    id: "Procnias_1",
    label: "Lord of Decibels",
    description: "Record 1 Procnias species",
    condition: (userData) => (userData.byGenus?.Procnias || 0) >= 1,
  },
  {
    id: "Bombycilla_1",
    label: "Drunk on Juniper",
    description: "Record 1 Bombycilla species",
    condition: (userData) => (userData.byGenus?.Bombycilla || 0) >= 1,
  },
  {
    id: "Casuarius_1",
    label: "Clever Girl",
    description: "Record 1 cassowary species",
    condition: (userData) => (userData.byGenus?.Casuarius || 0) >= 1,
  },

  {
    id: "Crotophaga_1",
    label: "Ani, are you ok? Are you ok, Ani?",
    description: "Record 1 Crotophaga species",
    condition: (userData) => (userData.byGenus?.Crotophaga || 0) >= 1,
  },
  {
    id: "Mimus_3",
    label: "The Troll",
    description: "Record 3 Mimus species",
    condition: (userData) => (userData.byGenus?.Mimus || 0) >= 3,
  },

  {
    id: "Rupicola_1",
    label: "The Exhibitionist",
    description: "Record 1 Rupicola species",
    condition: (userData) => (userData.byGenus?.Rupicola || 0) >= 1,
  },
  {
    id: "Pipreola_3",
    label: "Berry Good",
    description: "Record 1 Pipreola species",
    condition: (userData) => (userData.byGenus?.Pipreola || 0) >= 3,
  },

  // Species-specific

  {
    id: "bananquit_1",
    label: "This day is bananas. B-A-N-A-N-A-S!",
    description: "Record a Bananaquit",
    condition: (userData) =>
      (userData.bySpecies?.["Coereba flaveola"] || 0) >= 1,
  },

  {
    id: "Stilpnia_vitriolina_1",
    label: "I Don't Want No Scrubs",
    description: "Record a Scrub Tanager",
    condition: (userData) =>
      (userData.bySpecies?.["Tangara vitriolina"] || 0) >= 1,
  },
  {
    id: "Haliaetus_1",
    label: "Commander of Freedom",
    description: "Record a Bald Eagle",
    condition: (userData) =>
      (userData.bySpecies?.["Haliaeetus leucocephalus"] || 0) >= 1,
  },
  {
    id: "Panthera_onca_1",
    label: "King of the Neotropics",
    description: "Record a Jaguar",
    condition: (userData) => (userData.bySpecies?.["Panthera onca"] || 0) >= 1,
  },

  {
    id: "Corvus_corax_1",
    label: "Nevermore",
    description: "Record a Common Raven",
    condition: (userData) => (userData.bySpecies?.["Corvus corax"] || 0) >= 1,
  },

  {
    id: "Ursus_arctos_office",
    label: "Bears. Beets. Battlestar Galactica.",
    description: "Record a Brown Bear",
    condition: (userData) => (userData.bySpecies?.["Ursus arctos"] || 0) >= 1,
  },

  {
    id: "Cuculus_1",
    label: "Identity Theft Is Not a Joke!",
    description: "Record a Cuckoo",
    condition: (userData) => (userData.byGenus?.Cuculus || 0) >= 1,
  },

  {
    id: "Tapirus_bairdii_1",
    label: "Forest Ghost",
    description: "Record Baird's Tapir",
    condition: (userData) =>
      (userData.bySpecies?.["Tapirus bairdii"] || 0) >= 1,
  },

  {
    id: "Hydrochoerus_hydrochaeris_1",
    label: "Capybara!",
    description: "Record a Capybara",
    condition: (userData) =>
      (userData.bySpecies?.["Hydrochoerus hydrochaeris"] || 0) >= 1,
  },

  {
    id: "Balaenoptera_musculus_1",
    label: "The Largest Ever",
    description: "Record a Blue Whale",
    condition: (userData) =>
      (userData.bySpecies?.["Balaenoptera musculus"] || 0) >= 1,
  },

  {
    id: "Architeuthis_dux_1",
    label: "The Kraken",
    description: "Record a Giant Squid",
    condition: (userData) =>
      (userData.bySpecies?.["Architeuthis dux"] || 0) >= 1,
  },

  {
    id: "Sequoiadendron_giganteum_1",
    label: "Standing Among Giants",
    description: "Record a Giant Sequoia",
    condition: (userData) =>
      (userData.bySpecies?.["Sequoiadendron giganteum"] || 0) >= 1,
  },

  {
    id: "Rafflesia_arnoldii_1",
    label: "The World's Largest Flower",
    description: "Record Rafflesia arnoldii",
    condition: (userData) =>
      (userData.bySpecies?.["Rafflesia arnoldii"] || 0) >= 1,
  },

  {
    id: "Amphiprion_ocellaris_1",
    label: "Nemo",
    description: "Record an Ocellaris Clownfish",
    condition: (userData) =>
      (userData.bySpecies?.["Amphiprion ocellaris"] || 0) >= 1,
  },

  {
    id: "Carcharodon_carcharias_1",
    label: "Bruce",
    description: "Record a Great White Shark",
    condition: (userData) =>
      (userData.bySpecies?.["Carcharodon carcharias"] || 0) >= 1,
  },

  {
    id: "Phacochoerus_africanus_1",
    label: "Hakuna Matata",
    description: "Record a Common Warthog",
    condition: (userData) =>
      (userData.bySpecies?.["Phacochoerus africanus"] || 0) >= 1,
  },
  {
    id: "Perameles_nasuta_1",
    label: "Crash Bandicoot",
    description: "Record an Eastern Long-nosed Bandicoot",
    condition: (userData) =>
      (userData.bySpecies?.["Perameles nasuta"] || 0) >= 1,
  },

  {
    id: "Ailuropoda_melanoleuca_1",
    label: "Po",
    description: "Record a Giant Panda",
    condition: (userData) =>
      (userData.bySpecies?.["Ailuropoda melanoleuca"] || 0) >= 1,
  },

  {
    id: "Tachyglossus_aculeatus_1",
    label: "Knuckles",
    description: "Record a Short-beaked Echidna",
    condition: (userData) =>
      (userData.bySpecies?.["Tachyglossus aculeatus"] || 0) >= 1,
  },

  {
    id: "Erinaceus_europaeus_1",
    label: "Sonic",
    description: "Record a European Hedgehog",
    condition: (userData) =>
      (userData.bySpecies?.["Erinaceus europaeus"] || 0) >= 1,
  },

  {
    id: "Ursus_arctos_1",
    label: "Yogi",
    description: "Record a Brown Bear",
    condition: (userData) => (userData.bySpecies?.["Ursus arctos"] || 0) >= 1,
  },

  {
    id: "Geococcyx_californianus_1",
    label: "Bip! Bip!",
    description: "Record a Greater Roadrunner",
    condition: (userData) =>
      (userData.bySpecies?.["Geococcyx californianus"] || 0) >= 1,
  },

  {
    id: "Python_molurus_1",
    label: "Kaa",
    description: "Record an Indian Python",
    condition: (userData) => (userData.bySpecies?.["Python molurus"] || 0) >= 1,
  },

  {
    id: "Panthera_pardus_1",
    label: "Bagheera",
    description: "Record a Leopard",
    condition: (userData) =>
      (userData.bySpecies?.["Panthera pardus"] || 0) >= 1,
  },

  {
    id: "Suricata_suricatta_1",
    label: "Timón",
    description: "Record a Meerkat",
    condition: (userData) =>
      (userData.bySpecies?.["Suricata suricatta"] || 0) >= 1,
  },

  {
    id: "Rangifer_tarandus_1",
    label: "Rudy",
    description: "Record a Reindeer",
    condition: (userData) =>
      (userData.bySpecies?.["Rangifer tarandus"] || 0) >= 1,
  },

  {
    id: "Paracanthurus_hepatus_1",
    label: "Dory",
    description: "Record a Blue Tang",
    condition: (userData) =>
      (userData.bySpecies?.["Paracanthurus hepatus"] || 0) >= 1,
  },

  {
    id: "chile_endemic_tapaculos_3",
    label: "Tapakliao!",
    description: "Record 3 tapaculos endemic to Chile",
    condition: (userData) =>
      (userData.bySpecies?.["Pteroptochos megapodius"] || 0) >= 1 &&
      (userData.bySpecies?.["Scelorchilus albicollis"] || 0) >= 1 &&
      (userData.bySpecies?.["Scytalopus fuscus"] || 0) >= 1,
  },

  // 🧭 Exploration-style
  {
    id: "locations_5",
    label: "Local Explorer",
    description: "Record species in 5 locations",
    condition: (userData) => userData.totalLocations >= 5,
  },

  {
    id: "locations_15",
    label: "Regional Explorer",
    description: "Record species in 15 locations",
    condition: (userData) => userData.totalLocations >= 15,
  },

  {
    id: "canada_100",
    label: "100 Species, Eh?",
    description: "Record 100 species in Canada",
    condition: (userData) => (userData.byCountry?.canada || 0) >= 100,
  },

  {
    id: "united_states_100",
    label: "American Naturalist",
    description: "Record 100 species in the United States",
    condition: (userData) => (userData.byCountry?.united_states || 0) >= 100,
  },

  {
    id: "mexico_100",
    label: "Aztec Spirit",
    description: "Record 100 species in Mexico",
    condition: (userData) => (userData.byCountry?.mexico || 0) >= 100,
  },

  {
    id: "belize_100",
    label: "Maya's Legacy",
    description: "Record 100 species in Belize",
    condition: (userData) => (userData.byCountry?.belize || 0) >= 100,
  },

  {
    id: "guatemala_100",
    label: "100 Jades",
    description: "Record 100 species in Guatemala",
    condition: (userData) => (userData.byCountry?.guatemala || 0) >= 100,
  },

  {
    id: "honduras_100",
    label: "Keeper of Copán",
    description: "Record 100 species in Honduras",
    condition: (userData) => (userData.byCountry?.honduras || 0) >= 100,
  },

  {
    id: "el_salvador_100",
    label: "Child of Fire",
    description: "Record 100 species in El Salvador",
    condition: (userData) => (userData.byCountry?.el_salvador || 0) >= 100,
  },

  {
    id: "nicaragua_100",
    label: "100 Volcanic Souls",
    description: "Record 100 species in Nicaragua",
    condition: (userData) => (userData.byCountry?.nicaragua || 0) >= 100,
  },

  {
    id: "costa_rica_100",
    label: "100 Pura Vida",
    description: "Record 100 species in Costa Rica",
    condition: (userData) => (userData.byCountry?.costa_rica || 0) >= 100,
  },

  {
    id: "panama_100",
    label: "Pedro Cámara",
    description: "Record 100 species in Panama",
    condition: (userData) => (userData.byCountry?.panama || 0) >= 100,
  },

  {
    id: "bahamas_100",
    label: "Island Hopper",
    description: "Record 100 species in the Bahamas",
    condition: (userData) => (userData.byCountry?.bahamas || 0) >= 100,
  },

  {
    id: "cuba_100",
    label: "100 Pearls",
    description: "Record 100 species in Cuba",
    condition: (userData) => (userData.byCountry?.cuba || 0) >= 100,
  },

  {
    id: "jamaica_100",
    label: "100 Love",
    description: "Record 100 species in Jamaica",
    condition: (userData) => (userData.byCountry?.jamaica || 0) >= 100,
  },

  {
    id: "haiti_100",
    label: "Ayiti Libre",
    description: "Record 100 species in Haiti",
    condition: (userData) => (userData.byCountry?.haiti || 0) >= 100,
  },

  {
    id: "dominican_republic_100",
    label: "El Niagara en Bicicleta",
    description: "Record 100 species in the Dominican Republic",
    condition: (userData) =>
      (userData.byCountry?.dominican_republic || 0) >= 100,
  },

  {
    id: "trinidad_and_tobago_100",
    label: "100 Calypso Soul",
    description: "Record 100 species in Trinidad and Tobago",
    condition: (userData) =>
      (userData.byCountry?.trinidad_and_tobago || 0) >= 100,
  },

  {
    id: "dominica_100",
    label: "Emerald Wanderer",
    description: "Record 100 species in Dominica",
    condition: (userData) => (userData.byCountry?.dominica || 0) >= 100,
  },

  {
    id: "saint_lucia_100",
    label: "Child of the Pitons",
    description: "Record 100 species in Saint Lucia",
    condition: (userData) => (userData.byCountry?.saint_lucia || 0) >= 100,
  },

  {
    id: "saint_vincent_and_the_grenadines_100",
    label: "Soufrière Spirit",
    description: "Record 100 species in Saint Vincent and the Grenadines",
    condition: (userData) =>
      (userData.byCountry?.saint_vincent_and_the_grenadines || 0) >= 100,
  },

  {
    id: "barbados_100",
    label: "Bajan Pride",
    description: "Record 100 species in Barbados",
    condition: (userData) => (userData.byCountry?.barbados || 0) >= 100,
  },

  {
    id: "grenada_100",
    label: "Nutmeg Emperor",
    description: "Record 100 species in Grenada",
    condition: (userData) => (userData.byCountry?.grenada || 0) >= 100,
  },

  {
    id: "venezuela_100",
    label: "Tepui Wanderer",
    description: "Record 100 species in Venezuela",
    condition: (userData) => (userData.byCountry?.venezuela || 0) >= 100,
  },

  {
    id: "guyana_100",
    label: "100 Shields",
    description: "Record 100 species in Guyana",
    condition: (userData) => (userData.byCountry?.guyana || 0) >= 100,
  },

  {
    id: "suriname_100",
    label: "Rainforest Guardian",
    description: "Record 100 species in Suriname",
    condition: (userData) => (userData.byCountry?.suriname || 0) >= 100,
  },

  {
    id: "french_guiana_100",
    label: "Rocket Botanist",
    description: "Record 100 species in French Guiana",
    condition: (userData) => (userData.byCountry?.french_guiana || 0) >= 100,
  },

  {
    id: "ecuador_100",
    label: "Equator Walker",
    description: "Record 100 species in Ecuador",
    condition: (userData) => (userData.byCountry?.ecuador || 0) >= 100,
  },

  {
    id: "peru_100",
    label: "100 Andean Spirits",
    description: "Record 100 species in Peru",
    condition: (userData) => (userData.byCountry?.peru || 0) >= 100,
  },

  {
    id: "brazil_100",
    label: "Girl from BioIndex",
    description: "Record 100 species in Brazil",
    condition: (userData) => (userData.byCountry?.brazil || 0) >= 100,
  },

  {
    id: "bolivia_100",
    label: "100 Salar Dreams",
    description: "Record 100 species in Bolivia",
    condition: (userData) => (userData.byCountry?.bolivia || 0) >= 100,
  },

  {
    id: "paraguay_100",
    label: "Guaraní Guardian",
    description: "Record 100 species in Paraguay",
    condition: (userData) => (userData.byCountry?.paraguay || 0) >= 100,
  },

  {
    id: "chile_100",
    label: "100 Especies de Amor y un Chileno Desesperado",
    description: "Record 100 species in Chile",
    condition: (userData) => (userData.byCountry?.chile || 0) >= 100,
  },

  {
    id: "argentina_100",
    label: "Primavera 100",
    description: "Record 100 species in Argentina",
    condition: (userData) => (userData.byCountry?.argentina || 0) >= 100,
  },
  {
    id: "colombia_100",
    label: "100 Species of Solitude",
    description: "Record 100 species in Colombia",
    condition: (userData) => (userData.byCountry?.colombia || 0) >= 100,
  },

  {
    id: "illinois_30",
    label: "Chicagull",
    description: "Record 100 species in Illinois",
    condition: (userData) => (userData.byRegion?.illinois || 0) >= 30,
  },

  {
    id: "indiana_30",
    label: "Hoosier Daddy",
    description: "Record 30 species in Indiana, USA",
    condition: (userData) => (userData.byRegion?.indiana || 0) >= 30,
  },
  {
    id: "california_30",
    label: "Cali Forager",
    description: "Record 30 species in California",
    condition: (userData) => (userData.byRegion?.california || 0) >= 30,
  },

  {
    id: "florida_30",
    label: "Gator Country",
    description: "Record 30 species in Florida",
    condition: (userData) => (userData.byRegion?.florida || 0) >= 30,
  },

  {
    id: "texas_30",
    label: "Lone Star Naturalist",
    description: "Record 30 species in Texas",
    condition: (userData) => (userData.byRegion?.texas || 0) >= 30,
  },

  {
    id: "new_york_30",
    label: "Empire State of Biodiversity",
    description: "Record 30 species in New York",
    condition: (userData) => (userData.byRegion?.new_york || 0) >= 30,
  },

  {
    id: "hawaii_30",
    label: "Aloha ʻĀina",
    description: "Record 30 species in Hawaii",
    condition: (userData) => (userData.byRegion?.hawaii || 0) >= 30,
  },

  {
    id: "alaska_30",
    label: "Into the Wild",
    description: "Record 30 species in Alaska",
    condition: (userData) => (userData.byRegion?.alaska || 0) >= 30,
  },

  {
    id: "arizona_30",
    label: "Desert Bloom",
    description: "Record 30 species in Arizona",
    condition: (userData) => (userData.byRegion?.arizona || 0) >= 30,
  },

  {
    id: "colorado_30",
    label: "Rocky Mountain High",
    description: "Record 30 species in Colorado",
    condition: (userData) => (userData.byRegion?.colorado || 0) >= 30,
  },

  {
    id: "washington_30",
    label: "Evergreen Explorer",
    description: "Record 30 species in Washington",
    condition: (userData) => (userData.byRegion?.washington || 0) >= 30,
  },

  {
    id: "oregon_30",
    label: "Trail Blazer",
    description: "Record 30 species in Oregon",
    condition: (userData) => (userData.byRegion?.oregon || 0) >= 30,
  },
  {
    id: "amazonas_50",
    label: "Amazonas Prime",
    description: "Record 50 species in el departamento de Amazonas, Colombia",
    condition: (userData) => (userData.byRegion?.["amazonas"] || 0) >= 50,
  },

  {
    id: "antioquia_50",
    label: "¡Eh, Ave María pues!",
    description: "Record 50 species in el departamento de Antioquia, Colombia",
    condition: (userData) => (userData.byRegion?.["antioquia"] || 0) >= 50,
  },

  {
    id: "arauca_50",
    label: "Arauca Vibrador",
    description: "Record 50 species in el departamento de Arauca, Colombia",
    condition: (userData) => (userData.byRegion?.["arauca"] || 0) >= 50,
  },

  {
    id: "atlantico_50",
    label: "En Barranquilla Me Quedo",
    description: "Record 50 species in el departamento del Atlántico, Colombia",
    condition: (userData) => (userData.byRegion?.["atlántico"] || 0) >= 50,
  },

  {
    id: "bolivar_50",
    label: "Rebelión en el Corralito",
    description: "Record 50 species in el departamento de Bolívar, Colombia",
    condition: (userData) => (userData.byRegion?.["bolívar"] || 0) >= 50,
  },

  {
    id: "boyaca_50",
    label: "Sumercé Silvestre",
    description: "Record 50 species in el departamento de Boyacá, Colombia",
    condition: (userData) => (userData.byRegion?.["boyacá"] || 0) >= 50,
  },

  {
    id: "caldas_50",
    label: "Caldas del Alma",
    description: "Record 50 species in el departamento de Caldas, Colombia",
    condition: (userData) => (userData.byRegion?.["daldas"] || 0) >= 50,
  },

  {
    id: "caqueta_50",
    label: "Puerta de Oro, Pero Verde",
    description: "Record 50 species in el departamento de Caquetá, Colombia",
    condition: (userData) => (userData.byRegion?.["caquetá"] || 0) >= 50,
  },

  {
    id: "casanare_50",
    label: "Ay Sí Sí, Casanare",
    description: "Record 50 species in el departamento de Casanare, Colombia",
    condition: (userData) => (userData.byRegion?.["casanare"] || 0) >= 50,
  },

  {
    id: "cauca_50",
    label: "Pubenza Power",
    description: "Record 50 species in el departamento del Cauca, Colombia",
    condition: (userData) => (userData.byRegion?.["cauca"] || 0) >= 50,
  },

  {
    id: "cesar_50",
    label: "La Gota Fría",
    description: "Record 50 species in el departamento del Cesar, Colombia",
    condition: (userData) => (userData.byRegion?.["cesar"] || 0) >= 50,
  },

  {
    id: "choco_50",
    label: "De Donde Vengo Yo",
    description: "Record 50 species in el departamento del Chocó, Colombia",
    condition: (userData) => (userData.byRegion?.["chocó"] || 0) >= 50,
  },

  {
    id: "cordoba_50",
    label: "María Varilla",
    description: "Record 50 species in el departamento de Córdoba, Colombia",
    condition: (userData) => (userData.byRegion?.["córdoba"] || 0) >= 50,
  },

  {
    id: "cundinamarca_50",
    label: "El Dorado Existe",
    description:
      "Record 50 species in el departamento de Cundinamarca, Colombia",
    condition: (userData) => (userData.byRegion?.["cundinamarca"] || 0) >= 50,
  },

  {
    id: "guainia_50",
    label: "Muchas Aguas, Cero Excusas",
    description: "Record 50 species in el departamento de Guainía, Colombia",
    condition: (userData) => (userData.byRegion?.["guainía"] || 0) >= 50,
  },

  {
    id: "guaviare_50",
    label: "Jurassic Guaviare",
    description: "Record 50 species in el departamento del Guaviare, Colombia",
    condition: (userData) => (userData.byRegion?.["guaviare"] || 0) >= 50,
  },

  {
    id: "huila_50",
    label: "Sanjuanero Salvaje",
    description: "Record 50 species in el departamento del Huila, Colombia",
    condition: (userData) => (userData.byRegion?.["huila"] || 0) >= 50,
  },

  {
    id: "la_guajira_50",
    label: "Francisco el Hombre",
    description: "Record 50 species in el departamento de La Guajira, Colombia",
    condition: (userData) => (userData.byRegion?.["la guajira"] || 0) >= 50,
  },

  {
    id: "magdalena_50",
    label: "La Tierra del Olvido",
    description: "Record 50 species in el departamento del Magdalena, Colombia",
    condition: (userData) => (userData.byRegion?.["magdalena"] || 0) >= 50,
  },

  {
    id: "meta_50",
    label: "Ay, Mi Llanura",
    description: "Record 50 species in el departamento del Meta, Colombia",
    condition: (userData) => (userData.byRegion?.["meta"] || 0) >= 50,
  },

  {
    id: "narino_50",
    label: "La Guaneña",
    description: "Record 50 species in el departamento de Nariño, Colombia",
    condition: (userData) => (userData.byRegion?.["nariño"] || 0) >= 50,
  },

  {
    id: "norte_de_santander_50",
    label: "Brisas del Pamplonita",
    description:
      "Record 50 species in el departamento de Norte de Santander, Colombia",
    condition: (userData) =>
      (userData.byRegion?.["norte de santander"] || 0) >= 50,
  },

  {
    id: "putumayo_50",
    label: "Al Fin del Mundo",
    description: "Record 50 species in el departamento del Putumayo, Colombia",
    condition: (userData) => (userData.byRegion?.["putumayo"] || 0) >= 50,
  },

  {
    id: "quindio_50",
    label: "Modo Yipao",
    description: "Record 50 species in el departamento del Quindío, Colombia",
    condition: (userData) => (userData.byRegion?.["quindío"] || 0) >= 50,
  },

  {
    id: "risaralda_50",
    label: "Querendona y Trasnochadora",
    description: "Record 50 species in el departamento de Risaralda, Colombia",
    condition: (userData) => (userData.byRegion?.["risaralda"] || 0) >= 50,
  },

  {
    id: "san_andres_y_providencia_50",
    label: "Green Moon Rising",
    description:
      "Record 50 species in el departamento de San Andrés y Providencia, Colombia",
    condition: (userData) =>
      (userData.byRegion?.["san andrés y providencia"] || 0) >= 50,
  },

  {
    id: "santander_50",
    label: "Hormiga Culona Certified",
    description: "Record 50 species in el departamento de Santander, Colombia",
    condition: (userData) => (userData.byRegion?.["santander"] || 0) >= 50,
  },

  {
    id: "sucre_50",
    label: "Fiesta en Corraleja",
    description: "Record 50 species in el departamento de Sucre, Colombia",
    condition: (userData) => (userData.byRegion?.["sucre"] || 0) >= 50,
  },

  {
    id: "tolima_50",
    label: "Bunde y Lechona",
    description: "Record 50 species in el departamento del Tolima, Colombia",
    condition: (userData) => (userData.byRegion?.["tolima"] || 0) >= 50,
  },

  {
    id: "valle_del_cauca_50",
    label: "Oiga, Mire, Vea",
    description:
      "Record 50 species in el departamento del Valle del Cauca, Colombia",
    condition: (userData) =>
      (userData.byRegion?.["valle del cauca"] || 0) >= 50,
  },

  {
    id: "vaupes_50",
    label: "Yuruparí",
    description: "Record 50 species in el departamento del Vaupés, Colombia",
    condition: (userData) => (userData.byRegion?.["vaupés"] || 0) >= 50,
  },

  {
    id: "vichada_50",
    label: "Tuparro State of Mind",
    description: "Record 50 species in el departamento del Vichada, Colombia",
    condition: (userData) => (userData.byRegion?.["vichada"] || 0) >= 50,
  },
  {
    id: "aguascalientes_50",
    label: "Que Viva Aguascalientes",
    description: "Record 50 species in Aguascalientes, México",
    condition: (userData) => (userData.byRegion?.["aguascalientes"] || 0) >= 50,
  },

  {
    id: "baja_california_50",
    label: "La Rumorosa",
    description: "Record 50 species in Baja California, México",
    condition: (userData) =>
      (userData.byRegion?.["baja california"] || 0) >= 50,
  },

  {
    id: "baja_california_sur_50",
    label: "Donde Termina la Tierra",
    description: "Record 50 species in Baja California Sur, México",
    condition: (userData) =>
      (userData.byRegion?.["baja california sur"] || 0) >= 50,
  },

  {
    id: "campeche_50",
    label: "Piratas del Petén",
    description: "Record 50 species in Campeche, México",
    condition: (userData) => (userData.byRegion?.["campeche"] || 0) >= 50,
  },

  {
    id: "chiapas_50",
    label: "Palenque Confidential",
    description: "Record 50 species in Chiapas, México",
    condition: (userData) => (userData.byRegion?.["chiapas"] || 0) >= 50,
  },

  {
    id: "chihuahua_50",
    label: "El Estado Grande",
    description: "Record 50 species in Chihuahua, México",
    condition: (userData) => (userData.byRegion?.["chihuahua"] || 0) >= 50,
  },

  {
    id: "ciudad_de_mexico_50",
    label: "Chilango Power",
    description: "Record 50 species in Ciudad de México, México",
    condition: (userData) =>
      (userData.byRegion?.["ciudad de méxico"] || 0) >= 50,
  },

  {
    id: "coahuila_50",
    label: "Jurassic Coahuila",
    description: "Record 50 species in Coahuila, México",
    condition: (userData) => (userData.byRegion?.["coahuila"] || 0) >= 50,
  },

  {
    id: "colima_50",
    label: "Entre Volcanes y Palmeras",
    description: "Record 50 species in Colima, México",
    condition: (userData) => (userData.byRegion?.["colima"] || 0) >= 50,
  },

  {
    id: "durango_50",
    label: "Once Upon a Time in Durango",
    description: "Record 50 species in Durango, México",
    condition: (userData) => (userData.byRegion?.["durango"] || 0) >= 50,
  },

  {
    id: "guanajuato_50",
    label: "El Callejón del Bicho",
    description: "Record 50 species in Guanajuato, México",
    condition: (userData) => (userData.byRegion?.["guanajuato"] || 0) >= 50,
  },

  {
    id: "guerrero_50",
    label: "Quebrada Salvaje",
    description: "Record 50 species in Guerrero, México",
    condition: (userData) => (userData.byRegion?.["guerrero"] || 0) >= 50,
  },

  {
    id: "hidalgo_50",
    label: "Pastes, Prismas y Plantas",
    description: "Record 50 species in Hidalgo, México",
    condition: (userData) => (userData.byRegion?.["hidalgo"] || 0) >= 50,
  },

  {
    id: "jalisco_50",
    label: "Ay Jalisco, No Te Rajes",
    description: "Record 50 species in Jalisco, México",
    condition: (userData) => (userData.byRegion?.["jalisco"] || 0) >= 50,
  },

  {
    id: "mexico_50",
    label: "Mexiquense de Corazón",
    description: "Record 50 species in el Estado de México, México",
    condition: (userData) => (userData.byRegion?.["méxico"] || 0) >= 50,
  },

  {
    id: "michoacan_50",
    label: "Hasta la Monarca Siempre",
    description: "Record 50 species in Michoacán, México",
    condition: (userData) => (userData.byRegion?.["michoacán"] || 0) >= 50,
  },

  {
    id: "morelos_50",
    label: "Zapata Vive",
    description: "Record 50 species in Morelos, México",
    condition: (userData) => (userData.byRegion?.["morelos"] || 0) >= 50,
  },

  {
    id: "nayarit_50",
    label: "Nayarit, Qué Bonito",
    description: "Record 50 species in Nayarit, México",
    condition: (userData) => (userData.byRegion?.["nayarit"] || 0) >= 50,
  },

  {
    id: "nuevo_leon_50",
    label: "Cerro de la Silla Club",
    description: "Record 50 species in Nuevo León, México",
    condition: (userData) => (userData.byRegion?.["nuevo león"] || 0) >= 50,
  },

  {
    id: "oaxaca_50",
    label: "Dios Nunca Muere",
    description: "Record 50 species in Oaxaca, México",
    condition: (userData) => (userData.byRegion?.["oaxaca"] || 0) >= 50,
  },

  {
    id: "puebla_50",
    label: "Qué Chula es Puebla",
    description: "Record 50 species in Puebla, México",
    condition: (userData) => (userData.byRegion?.["puebla"] || 0) >= 50,
  },

  {
    id: "queretaro_50",
    label: "Peña de Bernal or Bust",
    description: "Record 50 species in Querétaro, México",
    condition: (userData) => (userData.byRegion?.["querétaro"] || 0) >= 50,
  },

  {
    id: "quintana_roo_50",
    label: "Caribe Mode",
    description: "Record 50 species in Quintana Roo, México",
    condition: (userData) => (userData.byRegion?.["quintana roo"] || 0) >= 50,
  },

  {
    id: "san_luis_potosi_50",
    label: "Huasteca Hasta la Muerte",
    description: "Record 50 species in San Luis Potosí, México",
    condition: (userData) =>
      (userData.byRegion?.["san luis potosí"] || 0) >= 50,
  },

  {
    id: "sinaloa_50",
    label: "El Sinaloense",
    description: "Record 50 species in Sinaloa, México",
    condition: (userData) => (userData.byRegion?.["sinaloa"] || 0) >= 50,
  },

  {
    id: "sonora_50",
    label: "Sonora Querida",
    description: "Record 50 species in Sonora, México",
    condition: (userData) => (userData.byRegion?.["sonora"] || 0) >= 50,
  },

  {
    id: "tabasco_50",
    label: "Edén de México",
    description: "Record 50 species in Tabasco, México",
    condition: (userData) => (userData.byRegion?.["tabasco"] || 0) >= 50,
  },

  {
    id: "tamaulipas_50",
    label: "Jaiba Salvaje",
    description: "Record 50 species in Tamaulipas, México",
    condition: (userData) => (userData.byRegion?.["tamaulipas"] || 0) >= 50,
  },

  {
    id: "tlaxcala_50",
    label: "Tlaxcala Sí Existe",
    description: "Record 50 species in Tlaxcala, México",
    condition: (userData) => (userData.byRegion?.["tlaxcala"] || 0) >= 50,
  },

  {
    id: "veracruz_50",
    label: "Lafourcade Core",
    description: "Record 50 species in Veracruz, México",
    condition: (userData) => (userData.byRegion?.["veracruz"] || 0) >= 50,
  },

  {
    id: "yucatan_50",
    label: "¡Bomba!",
    description: "Record 50 species in Yucatán, México",
    condition: (userData) => (userData.byRegion?.["yucatán"] || 0) >= 50,
  },

  {
    id: "zacatecas_50",
    label: "Marcha de Zacatecas",
    description: "Record 50 species in Zacatecas, México",
    condition: (userData) => (userData.byRegion?.["zacatecas"] || 0) >= 50,
  },
  {
    id: "Pandion_haliaetus_chile_1",
    label: "Osprey al Sur",
    description: "Record an Osprey in Chile",
    condition: (userData) =>
      (userData.bySpeciesCountry?.["Pandion haliaetus"]?.chile || 0) >= 1,
  },
  {
    id: "yukon_30",
    label: "The Call of the Wild",
    description: "Record 30 species in Yukon, Canada",
    condition: (userData) => (userData.byRegion?.["Yukon"] || 0) >= 30,
  },
  {
    id: "quebec_30",
    label: "Je Me Souviens... des Espèces",
    description: "Record 30 species in Quebec, Canada",
    condition: (userData) => (userData.byRegion?.["Quebec"] || 0) >= 30,
  },
];

// 🔎 Helper: get unlocked titles (pure, no DB)
export function evaluateTitles(userData) {
  return TITLES.filter((t) => t.condition(userData));
}

// Resolve stored achievement snapshots against the current title definitions.
export function resolveTitle(titleId, storedLabel = "") {
  const normalizedTitleId = String(titleId || "").trim();
  const normalizedStoredLabel = String(storedLabel || "")
    .trim()
    .toLowerCase();

  const titleById = TITLES.find((title) => title.id === normalizedTitleId);
  if (titleById) return titleById;

  if (!normalizedStoredLabel) return null;

  return (
    TITLES.find((title) =>
      (title.legacyLabels || []).some(
        (legacyLabel) =>
          String(legacyLabel || "")
            .trim()
            .toLowerCase() === normalizedStoredLabel,
      ),
    ) || null
  );
}

// 🔎 Helper: get only NEW titles (vs already unlocked)
export function getNewTitles(userData, unlockedIds = []) {
  const unlockedSet = new Set(unlockedIds);

  return TITLES.filter((t) => t.condition(userData) && !unlockedSet.has(t.id));
}
